/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging";
import { NetworkResource, NetworkResourceFailureReason } from "../../enums";
import {
  NetworkResourceNode,
  NodeStateChangeCallback
} from "../NetworkResourceNode";

// Debounce to handle transient flapping
const NETWORK_INTERFACE_DEBOUNCE_MS: number = 800;

/**
 * Node tracking network interface (LAN) availability.
 */
class NetworkInterfaceNode extends NetworkResourceNode {
  protected static override readonly TAG: string = "NetworkInterfaceNode";

  private mDebounceTimerId: ReturnType<typeof setTimeout> | null = null;

  private mHandleNetworkEvent: (() => void) | null = null;

  constructor(onStateChanged: NodeStateChangeCallback) {
    super(NetworkResource.NETWORK_INTERFACE, onStateChanged);
  }

  /**
   * Determines initial NI state synchronously from navigator.onLine
   * and registers platform event listeners.
   */
  public override init(): void {
    super.init();

    const isConnected =
      typeof navigator !== "undefined" && navigator.onLine === true;

    Log.i(
      NetworkInterfaceNode.TAG,
      "init() initial onLine=" + String(isConnected)
    );

    if (isConnected) {
      this.setAvailability(true, null);
    } else {
      this.setAvailability(
        false,
        NetworkResourceFailureReason.NETWORK_INTERFACE_DOWN
      );
    }

    this.mHandleNetworkEvent = (): void => {
      if (this.mDebounceTimerId !== null) {
        clearTimeout(this.mDebounceTimerId);
      }

      this.mDebounceTimerId = setTimeout(() => {
        this.mDebounceTimerId = null;
        const isOnline =
          typeof navigator !== "undefined" && navigator.onLine === true;
        this.onNetworkResourceAvailabilityHint(
          NetworkResource.NETWORK_INTERFACE,
          isOnline,
          isOnline ? null : NetworkResourceFailureReason.NETWORK_INTERFACE_DOWN
        ).catch((error: unknown): void => {
          Log.e(
            NetworkInterfaceNode.TAG,
            "init() debounce hint dispatch failed",
            error
          );
        });
      }, NETWORK_INTERFACE_DEBOUNCE_MS);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("online", this.mHandleNetworkEvent);
      window.addEventListener("offline", this.mHandleNetworkEvent);
    } else {
      Log.e(
        NetworkInterfaceNode.TAG,
        "init() window is undefined, cannot register event listeners"
      );
    }
  }

  /**
   * Re-examines platform NI state. Used by the orchestrator when
   * a re-evaluate rule is applied.
   * @returns A promise that resolves when re-examination is complete
   */
  public override async invalidateState(): Promise<void> {
    Log.d(NetworkInterfaceNode.TAG, "invalidateState() re-examining NI state");
    const isConnected =
      typeof navigator !== "undefined" && navigator.onLine === true;

    if (isConnected) {
      this.setAvailability(true, null);
    } else {
      this.setAvailability(
        false,
        NetworkResourceFailureReason.NETWORK_INTERFACE_DOWN
      );
    }
  }

  public override stopInvalidation(): void {
    if (this.mDebounceTimerId !== null) {
      clearTimeout(this.mDebounceTimerId);
      this.mDebounceTimerId = null;
    }

    if (this.mHandleNetworkEvent !== null) {
      window.removeEventListener("online", this.mHandleNetworkEvent);
      window.removeEventListener("offline", this.mHandleNetworkEvent);
      this.mHandleNetworkEvent = null;
    }
  }

  /**
   * Receives an external availability hint for the network interface.
   * Accepts the hint directly as the new state — NI availability is
   * determined by platform signals, not by active probing.
   * @param resource - Must be NETWORK_INTERFACE; ignored otherwise
   * @param isAvailable - The hinted availability state
   * @param reason - The failure reason if unavailable; null otherwise
   * @returns A promise that resolves when the hint has been processed
   */
  public override async onNetworkResourceAvailabilityHint(
    resource: NetworkResource,
    isAvailable: boolean,
    reason: NetworkResourceFailureReason | null
  ): Promise<void> {
    if (resource !== NetworkResource.NETWORK_INTERFACE) {
      return;
    }

    Log.i(
      NetworkInterfaceNode.TAG,
      "onNetworkResourceAvailabilityHint() available=" +
        String(isAvailable) +
        ", reason=" +
        String(reason)
    );

    if (isAvailable) {
      this.setAvailability(true, null);
    } else {
      this.setAvailability(
        false,
        reason !== null
          ? reason
          : NetworkResourceFailureReason.NETWORK_INTERFACE_DOWN
      );
    }
  }
}

export { NetworkInterfaceNode };
